import { Layers } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import LightModeIcon from "@mui/icons-material/LightMode";
import { Box, Grid2 } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { addHours, differenceInMilliseconds } from "date-fns";
import format from "date-fns/format";
import isPast from "date-fns/isPast";
import { DateTime } from "luxon";
import React, {useEffect, useMemo, useRef, useState} from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../utils/date-locale";
import { gutterWidth, minTimeSlotHeight, shiftSpacing } from "./constants";
import { TimeSlot } from "./styled-components";
import { calculateIndicatorPosition, getShiftEffectiveStartDay } from "./utils";

const MS_PER_HOUR = 3600000;
const MIN_MARGIN = 50;
const MIN_WIDTH = shiftSpacing * 2;
const MIN_DIFF_MINUTES = 9;

const TimeSlotContainer = ({
  openCreateNewShiftDialog,
  canCreateOrEditShift,
  dayIndex,
  todayPosition,
  hourIndex,
  day,
  timeSlotHeight,
  allPassingShifts,
  openLayerPopover,
  selectedHourDateInfo,
  offsetStartTime = 0, // Default offset start time
  isDragging = false,
  ...props
}: {
  openCreateNewShiftDialog: any;
  canCreateOrEditShift: boolean;
  dayIndex: number;
  todayPosition: number;
  hourIndex: number;
  day: Date;
  timeSlotHeight: number;
  allPassingShifts: any;
  openLayerPopover: any;
  selectedHourDateInfo: any;
  offsetStartTime?: number;
  isDragging?: boolean;
} & React.ComponentProps<typeof Grid2>) => {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
  const [currentTimeHourIndex, setCurrentTimeHourIndex] = useState(-1);
  const timeoutIdRef = useRef<any>(null); // Use a ref to store the timeout ID
  const isFirstRender = useRef(true); // Track the first render

  // One hour string based on hourIndex (0-23) like "4:00 AM - 5:00 AM"
  const timeDateInfo = useMemo(() => {
    const startHour = hourIndex;
    const endHour = hourIndex + 1;
    // Change military time to standard time
    let startHourString = startHour % 12 === 0 ? "12" : `${startHour % 12}`;
    let endHourString = endHour % 12 === 0 ? "12" : `${endHour % 12}`;
    // Add AM and PM
    if (startHour < 12) {
      startHourString = startHourString + " AM";
    } else {
      startHourString = startHourString + " PM";
    }
    if (endHour < 12) {
      endHourString = endHourString + " AM";
    } else {
      endHourString = endHourString + " PM";
    }
    return {
      hourText: `${startHourString} - ${endHourString}`,
      dayText: format(day, "EEE", { locale: dateLocale }) ?? "-",
      dateText: format(day, "dd") ?? "-",
    };
  }, [hourIndex, day, dateLocale]);

  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset hover state when mouse leaves quickly (handles fast cursor movements)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseLeave = () => setIsHovered(false);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseleave", handleMouseLeave);
      setIsHovered(false);
    };
  }, []);

  const { offsetHours, adjustedHourIndex } = useMemo(() => {
    const hours = Math.floor(offsetStartTime / MS_PER_HOUR);
    return {
      offsetHours: hours,
      adjustedHourIndex: (hourIndex + hours) % 24
    };
  }, [offsetStartTime, hourIndex]);

  const isInPast = isPast(addHours(new Date(day), offsetHours));
  const isToday = todayPosition === dayIndex;

  const effectiveToday = getShiftEffectiveStartDay(new Date(), offsetStartTime); // Adjust current date with offset
  const isPastHour = useMemo(
    () => isInPast && hourIndex - 1 < effectiveToday.getHours(),
    [isInPast, hourIndex, effectiveToday]
  );

  const handleClick = () => {
    if (canCreateOrEditShift) {
      openCreateNewShiftDialog(new Date(dateWithHour));
    }
  };

  const dateWithHour = useMemo(() => {
    const date = new Date(day);
    if (adjustedHourIndex < hourIndex) {
      date.setDate(date.getDate() + 1);
    }
    date.setHours(adjustedHourIndex, 0, 0, 0);
    return date;
  }, [adjustedHourIndex, day, hourIndex]);

  const isDayLightSavingHour = useMemo(() => {
    if (!dateWithHour) {
      return { isDayLightSavingHour: false, shiftDirection: 0 };
    }

    const dt = DateTime.fromJSDate(new Date(dateWithHour));

    const currentOffset = dt.offset;
    const nextOffset = dt.plus({ hours: 1 }).offset;
    const shiftDirection = nextOffset - currentOffset;

    const isDayLightSavingHour = shiftDirection !== 0;

    return { isDayLightSavingHour, shiftDirection: -shiftDirection };
  },[dateWithHour]);

  const validAllPassingShifts = useMemo(() => {
    if (!allPassingShifts || allPassingShifts.length <= 1) return allPassingShifts;
    return allPassingShifts.filter((shift: any) => {
      return !(shift.shiftEndHourIndex === offsetHours &&
        shift.shiftNextDayIndex === dayIndex &&
        allPassingShifts.length > 1);
    });
  }, [allPassingShifts, dayIndex, offsetHours]);

  // Check if allPassingShifts is empty or not by evaluating allPassingShifts contain children or not
  const isPropsEmpty =
    (Array.isArray(props.children) ? props.children.length === 0 : true) &&
    (!Array.isArray(validAllPassingShifts)
      || validAllPassingShifts.length === 0
      || validAllPassingShifts.every((shift: any) => shift.shiftEndHourIndex === hourIndex + offsetHours - 1)
    );
  let canShowLayerIcon = false;

  const isClicked =
    selectedHourDateInfo?.hourIndex === hourIndex &&
    selectedHourDateInfo?.dayIndex === dayIndex;
  if (isClicked) {
    // Show layer icon when clicked
    canShowLayerIcon = true;
  }

  const canShowAddIcon =
    isHovered &&
    !isDragging &&
    !(isInPast && !isToday) &&
    !(isToday && (isPastHour|| currentTimeHourIndex >= hourIndex));

  // Display layer icon if shifts are eclipsing each other under 10min
  if (!canShowLayerIcon && validAllPassingShifts?.length >= 2) {
    for (let i = 0; i < validAllPassingShifts.length - 1; i++) {
      const currentShift = validAllPassingShifts[i];
      const nextShift = validAllPassingShifts[i + 1];

      const diffMinutes = (
        new Date(nextShift.startDateTime).getTime() -
        new Date(currentShift.startDateTime).getTime()
      ) / 60000;

      if (diffMinutes <= MIN_DIFF_MINUTES) {
        // Show layer icon if both shifts have large margins, small widths, and similar margins (stacked beyond visible area)
        if (
          currentShift.changedMarginOfShift > MIN_MARGIN &&
          currentShift.shiftWidthPercent <= MIN_WIDTH &&
          nextShift.changedMarginOfShift > MIN_MARGIN &&
          nextShift.shiftWidthPercent <= MIN_WIDTH &&
          Math.abs(currentShift.changedMarginOfShift - nextShift.changedMarginOfShift) <= 5
        ) {
          canShowLayerIcon = true;
          break;
        }
        // Show layer icon only if shifts have similar margins (within 5px or 2% tolerance)
        // and one shift is visually eclipsed by another (95%+ overlap in width)
        const marginDiff = Math.abs(currentShift.changedMarginOfShift - nextShift.changedMarginOfShift);
        if (marginDiff <= 5) {
          const largerWidth = Math.max(currentShift.shiftWidthPercent, nextShift.shiftWidthPercent);
          const smallerWidth = Math.min(currentShift.shiftWidthPercent, nextShift.shiftWidthPercent);
          if (largerWidth > 0 && smallerWidth / largerWidth >= 0.95) {
            canShowLayerIcon = true;
            break;
          }
        }
      }
    }
  }

  useEffect(() => {
    const calculateIndex = () => {
      const newCurrentTimeHourIndex = Math.floor(
        calculateIndicatorPosition(new Date(), timeSlotHeight, offsetStartTime) / timeSlotHeight
      );
      if (currentTimeHourIndex !== newCurrentTimeHourIndex) {
        setCurrentTimeHourIndex(newCurrentTimeHourIndex);
      }
    };

    const scheduleNextUpdate = () => {
      const offsetHours = Math.floor(offsetStartTime / (1000 * 60 * 60));
      const adjustedHourIndex = (hourIndex + offsetHours) % 24;
      const timeUntilTarget = differenceInMilliseconds(
        new Date(day).setHours(adjustedHourIndex, 0, 0, 0),
        new Date()
      );

      if (timeUntilTarget > 0) {
        // Clear any existing timeout before setting a new one
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }

        timeoutIdRef.current = setTimeout(() => {
          calculateIndex();
          timeoutIdRef.current = null; // Clear the ref after the timeout fires
        }, timeUntilTarget);
      } else {
        calculateIndex();
      }
    };
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (
      currentTimeHourIndex === -1 &&
      timeoutIdRef.current === null &&
      isToday &&
      !isPastHour
    ) {
      scheduleNextUpdate();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Page is now hidden
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      } else {
        // Page is now visible
        if (
          currentTimeHourIndex === -1 &&
          timeoutIdRef.current === null &&
          isToday &&
          !isPastHour
        ) {
          scheduleNextUpdate(); // Restart your timeout logic
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => { // Cleanup function
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null; // Good practice to also clear the ref
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimeHourIndex, timeSlotHeight, day, isToday, isPastHour, hourIndex, offsetStartTime]);


  if ((isInPast && !isToday) || (isToday && (isPastHour || currentTimeHourIndex >= hourIndex)) || !isPropsEmpty) {
    return (
      <TimeSlot
        {...props}
        ref={containerRef}
        height={timeSlotHeight}
        boxShadow={
          selectedHourDateInfo?.hourIndex === hourIndex &&
          selectedHourDateInfo.dayIndex === dayIndex
            ? `inset 0 0 0 ${timeSlotHeight / 2}px #2F4D8029`
            : "none"
        }
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor:
              (isToday && (isPastHour || currentTimeHourIndex >= hourIndex)) ||
              (isDayLightSavingHour.isDayLightSavingHour &&
                isDayLightSavingHour.shiftDirection < 0)
                ? "rgba(0,0,0,0.02)"
                : "transparent",
          }}
        >
          {props.children}
          {!isPropsEmpty && isDayLightSavingHour.shiftDirection >= 0 ? (
            <Box
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: `${gutterWidth}px`,
                height: `${timeSlotHeight}px`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    !isToday && isInPast
                      ? "rgba(0,0,0,0.02)"
                      : "rgba(0,0,0,0.02)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  marginTop: "1px",
                  marginBottom: "1px",
                }}
              >
                {canShowAddIcon && (
                  <IconButton
                    aria-label="add"
                    size="small"
                    sx={{
                      width: `${gutterWidth}px`,
                      height: `${(minTimeSlotHeight - 2) / 2}px`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleClick}
                  >
                    <AddIcon
                      sx={{
                        fontSize: `${(gutterWidth * 4.5) / 5}px`,
                        color: "#2F4D80",
                      }}
                    />
                  </IconButton>
                )}
                {/* Spacer */}
                {!canShowAddIcon && (
                  <Box
                    sx={{
                      width: `${gutterWidth}px`,
                      height: `${(minTimeSlotHeight - 2) / 2}px`,
                    }}
                  />
                )}
                {((isHovered && !isDragging && validAllPassingShifts.length >= 2) ||
                  canShowLayerIcon ||
                  (isDayLightSavingHour.isDayLightSavingHour &&
                    isDayLightSavingHour.shiftDirection > 0)) && (
                  <Tooltip
                    title={
                      <>
                        {isDayLightSavingHour.shiftDirection > 0
                          ? `${t("flex-scheduler_daylight-saving-hour")} : ${t("flex-scheduler_fall-back")}`
                          : ""}
                        {isDayLightSavingHour.isDayLightSavingHour &&
                          validAllPassingShifts.length > 1 && <br />}
                        {validAllPassingShifts.length > 1
                          ? t("flex-scheduler_stacked-shifts", { count: validAllPassingShifts.length })
                          : ""}
                      </>
                    }
                    disableInteractive
                  >
                    <IconButton
                      aria-label="view-shift"
                      size="small"
                      sx={{
                        width: `${gutterWidth}px`,
                        height: `${(minTimeSlotHeight - 2) / 2}px`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        if (validAllPassingShifts.length > 1) {
                          openLayerPopover(
                            e,
                            validAllPassingShifts,
                            hourIndex,
                            dayIndex,
                            timeDateInfo
                          );
                        }
                      }}
                    >
                      {isDayLightSavingHour.shiftDirection > 0 ? (
                        <LightModeIcon
                          sx={{
                            fontSize: `${(gutterWidth * 4.5) / 5}px`,
                            color: "#2F4D80",
                          }}
                        />
                      ) : (
                        <Layers
                          sx={{
                            fontSize: `${(gutterWidth * 7) / 9}px`,
                            color: "#2F4D80",
                          }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: `${gutterWidth}px`,
                height: `${timeSlotHeight}px`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isDayLightSavingHour.isDayLightSavingHour ? (
                <Tooltip
                  title={`${t("flex-scheduler_daylight-saving-hour")} : ${
                    isDayLightSavingHour.shiftDirection > 0
                      ? t("flex-scheduler_fall-back")
                      : t("flex-scheduler_spring-forward")
                  }`}
                >
                  <LightModeIcon
                    sx={{
                      fontSize: `${(gutterWidth * 4.5) / 5}px`,
                      color: "#2F4D80",
                    }}
                  />
                </Tooltip>
              ) : (
                <Box
                  sx={{
                    backgroundColor:
                      !isToday && isInPast
                        ? "rgba(0,0,0,0.02)"
                        : "rgba(0,0,0,0.02)",
                    height: "100%",
                    width: "100%",
                    marginTop: "1px",
                    marginBottom: "1px",
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </TimeSlot>
    );
  } else {
    return (
      <TimeSlot
        {...props}
        ref={containerRef}
        height={timeSlotHeight}
        sx={{
          ...props.sx,
          cursor: canCreateOrEditShift ? "pointer" : "default",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor:
              (isToday && (isPastHour || currentTimeHourIndex >= hourIndex)) ||
                (isDayLightSavingHour.isDayLightSavingHour && isDayLightSavingHour.shiftDirection < 0)
                ? "rgba(0,0,0,0.02)"
                : isHovered && canCreateOrEditShift && !isDragging
                  ? "rgb(251,251,253)"
                  : "#fff",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: `${gutterWidth}px`,
            height: `${timeSlotHeight}px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: isHovered ? "transparent" : "rgb(251,251,253)",
                height: "100%",
                width: "100%",
                marginTop: "1px",
                marginBottom: "1px",
              }}
          >
            {isDayLightSavingHour.isDayLightSavingHour ?
                <Tooltip title={`${t("flex-scheduler_daylight-saving-hour")} : ${isDayLightSavingHour.shiftDirection < 0
                    ? t("flex-scheduler_spring-forward") : t("flex-scheduler_fall-back")}`}>
                  <LightModeIcon
                      sx={{
                        fontSize: `${(gutterWidth * 4.5) / 5}px`,
                        color: "#2F4D80",
                      }}
                  />
                </Tooltip>
                : null}
          </Box>
        </Box>
      </TimeSlot>
    );
  }
};

export default React.memo(TimeSlotContainer);
